import {
  applyMovementTruth,
  commitMovementTruth,
  isMovementTruthAcceptedResult,
  MovementTruthRejected,
  MovementTruthTerminal,
  type ActorTruthCommandBase,
  type MovementTruthAcceptedResult,
  type MovementTruthCommand,
  type MovementTruthChange,
  type MovementTruthRejectedResult,
  type MovementTruthRow,
  type MovementTruthTerminalInfo,
} from './client-truth-commit.js';
import { observeLocalTruthCommit } from '../telemetry/truth-commit-observer';
import { asRow } from '../minigames/rows';
import type { ActionComputedDelta, ComputedMember } from '../compile/action-computed-delta';
import { afterActionPaint } from '../../shared/after-action-paint';

/** A decided save with its own CAS shape, ordered alongside revision-based actions. */
export interface AppliedTruthSave {
  commit: () => Promise<unknown>;
  failed: (info: MovementTruthTerminalInfo) => void | Promise<void>;
  parked?: () => void;
}

export type MovementTransport = 'commit';
export type MovementDeliveryState = 'queued' | 'sending' | 'failed' | 'delivered';
/** Local result, separate from immutable JSON save bytes and their fingerprint.
 * U0's memberAppliedFrame can carry `computed` without serializing it on apply. */
export interface ActorApplyResult extends Record<MovementTransport, Promise<unknown>> { computed: ActionComputedDelta }
export interface MovementDelivery {
  operationId: string;
  commit: MovementDeliveryState;
  errors: Partial<Record<MovementTransport, unknown>>;
}
/** A background rest found its desired values already saved. No revision was written by this request. */
export interface AlreadyRestedResult extends MovementTruthAcceptedResult {
  alreadyRested: true;
}
interface Entry<C extends ActorTruthCommandBase = MovementTruthCommand> {
  save?: AppliedTruthSave;
  command: C;
  fingerprint: string;
  delivery: MovementDelivery;
  resolve: (result: unknown) => void;
  completion: ActorApplyResult;
  /** Shared actor projection after this entry's optimistic apply, across every schema. */
  projection?: MovementTruthChange;
  /** Automatic re-sends spent on a retryable (network / 5xx) failure of this entry. */
  autoRetries?: number;
}
/** Fix-bug 2026-10-09 (owner: "moving to attack reverted me and no attack"): a save that met DB lock contention answers a retryable
 * 503 `busy` (or the 20 s abort fires) — re-send the SAME operation on this schedule before parking the head for the manual
 * "Retry delivery" button. Everything queued behind it (the approach's attack) waits for it either way. Safe to repeat: the receivers
 * replay an operation id that already committed (movement_last_operation_id / receipts) and bind it to one payload fingerprint. */
const AUTO_RETRY_DELAYS_MS: readonly number[] = [1000, 2500, 5000];

/** One actor's ordered commit outbox.
 * A network-failed head stays queued with the SAME operation id: it is re-sent on a short
 * automatic schedule (AUTO_RETRY_DELAYS_MS), then waits for an explicit retry. A stale rejection or a definitive refusal is terminal: it is reconciled by
 * the adapter and it invalidates revision-dependent queued commands for this actor,
 * because those were resolved from this entry's optimistic destination. Later moves may
 * proceed from the authoritative revision. No path falls back to gameplay PHP.
 */
/** ONE queue per actor, shared by movement AND actions (A1): both are ordered by the same per-actor
 * revision, so a Dodge queued behind a walk carries the walk's optimistic revision and dies with it. */
export class MovementTruthOutbox<C extends ActorTruthCommandBase = MovementTruthCommand> {
  private readonly entries: Entry<C>[] = [];
  private readonly appliedSaveWaiters = new Set<() => void>();
  private running = false;

  constructor(private readonly options: {
    encounterId: string;
    actorId: string;
    row: () => MovementTruthRow;
    render: (command: C, row: MovementTruthRow) => void;
    onRenderError: (error: unknown, command: C, row: MovementTruthRow) => void;
    commit: (command: C) => Promise<unknown>;
    accepted: (command: C, result: MovementTruthAcceptedResult) => void;
    rejected: (command: C, result: MovementTruthRejectedResult) => void;
    /** Definitive non-stale refusal of the head (and, with `chained`, of each entry behind it). */
    failed?: (command: C, info: MovementTruthTerminalInfo) => void;
    changed: (delivery: MovementDelivery) => void;
    /** Optimistic in-place apply per schema; omitted = movement's default (`updates`). */
    apply?: (row: MovementTruthRow, command: C, optimisticRev: number) => void;
    /** Resolve additional truth before the request is fingerprinted and frozen. */
    prepare?: (command: C, row: MovementTruthRow) => void;
    computedMembers?: (command: C, row: MovementTruthRow) => Iterable<ComputedMember>;
    computedUnchanged?: (command: C) => boolean;
    /** Called whenever the queue has NO entry owed a server outcome any more (after a delivery, a stale
     * rejection or a terminal refusal): the adapter reconciles the row to confirmed truth — state a saved
     * frame merged while this queue was pending is only rendered now (Codex M1c review A3, finding 9). */
    settled?: () => void;
    /** Re-send schedule for a retryable failure of the head (default AUTO_RETRY_DELAYS_MS; [] = manual retry only). */
    autoRetryDelaysMs?: readonly number[];
  }) {
    const render = options.render;
    options.render = (command, row) => {
      const previous = globalThis.ftActionComputed;
      globalThis.ftActionComputed = { operationId: command.operation_id, delta: this.appliedResult(command.operation_id)?.computed ?? {} };
      try { render(command, row); } finally { globalThis.ftActionComputed = previous; }
    };
    const members = options.computedMembers;
    if (members) options.computedMembers = (command, row) => {
      try { return members(command, row); }
      catch (error) { try { options.onRenderError(error, command, row); } catch { /* observer only */ } return [[command.actor_id, row]]; }
    };
  }
  /** Re-send a retryable-failed head after the next delay of its schedule — only if it is still the failed head then. */
  private scheduleAutoRetry(entry: Entry<C>): void {
    const delays = this.options.autoRetryDelaysMs ?? AUTO_RETRY_DELAYS_MS;
    const spent = entry.autoRetries ?? 0;
    if (spent >= delays.length) return;
    entry.autoRetries = spent + 1;
    setTimeout(() => {
      const head = this.entries.find(item => item.delivery.commit !== 'delivered' && !this.isTerminal(item));
      if (head === entry && entry.delivery.commit === 'failed') this.retry('commit');
    }, delays[spent]);
  }
  private settle(): void {
    if (this.pending() === 0 && this.options.settled) { try { this.options.settled(); } catch { /* observer only */ } }
  }

  enqueue(command: C): ActorApplyResult {
    if (command.encounter_id !== this.options.encounterId || command.actor_id !== this.options.actorId) {
      throw new Error('Movement outbox actor mismatch');
    }
    this.options.prepare?.(command, this.options.row());
    const fingerprint = JSON.stringify(command);
    const existing = this.entries.find(entry => entry.command.operation_id === command.operation_id);
    if (existing) {
      if (existing.fingerprint !== fingerprint) throw new Error('Movement operation id reused with different data');
      return existing.completion;
    }
    if (this.entries.filter(entry => entry.delivery.commit !== 'delivered' && !this.isTerminal(entry)).length >= 128) {
      throw new Error('Movement deliveries pending; reconcile before moving again');
    }
    const row = this.options.row();
    const currentRev = Number(row.encounter_creatures.movement_rev ?? 0);
    if (!Number.isInteger(command.expected_rev) || command.expected_rev < 0 || currentRev !== command.expected_rev) {
      throw new Error('Movement input changed before commit: movement_rev');
    }
    for (const key of ['x_pos', 'y_pos', 'z_pos', 'movement_pct', 'aps'] as const) {
      const expected = command.before[key];
      if (expected === undefined) continue;
      // FT3D_FLOORS §2 / build_pos_arr: omitted persisted coordinates mean zero.
      // Thin snapshots omit the entire position bucket for an all-zero origin. The lean row builder
      // elides `movement_pct` (and a 0 scalar) the same way — absent means zero, never "unknown".
      const raw = key.endsWith('_pos') ? row.position?.[key] ?? row.encounter_creatures[key] : row.encounter_creatures[key];
      const actual = raw == null || raw === '' ? 0 : (Array.isArray(raw) ? raw[0] : raw);
      if (!Number.isFinite(expected) || Number(actual) !== expected) {
        throw new Error('Movement input changed before commit: ' + key);
      }
    }
    // Own immutable request bytes while asynchronous transport is pending.
    const snapshot = JSON.parse(fingerprint) as C;
    Object.freeze(snapshot.before);
    if (snapshot.updates) Object.freeze(snapshot.updates);
    if (Array.isArray(snapshot.route)) { snapshot.route.forEach(Object.freeze); Object.freeze(snapshot.route); }
    Object.freeze(snapshot);
    let resolve!: (result: unknown) => void;
    const completion: ActorApplyResult = { commit: new Promise<unknown>(done => { resolve = done; }), computed: {} };
    const entry: Entry<C> = { command: snapshot, fingerprint, resolve, completion,
      delivery: { operationId: command.operation_id, commit: 'queued', errors: {} } };
    this.entries.push(entry);
    let started;
    try {
      started = commitMovementTruth<C>({ command: snapshot, row, render: this.options.render,
        onRenderError: this.options.onRenderError, commit: async () => { await this.pump(); }, apply: this.options.apply,
        computedUnchanged: this.options.computedUnchanged?.(snapshot),
        computedMembers: this.options.computedMembers?.(snapshot, row), onComputed: computed => { completion.computed = computed; } });
      const value = (raw: unknown): number => Number(Array.isArray(raw) ? raw[0] : raw ?? 0);
      entry.projection = {
        x_pos: value(row.position?.x_pos ?? row.encounter_creatures.x_pos),
        y_pos: value(row.position?.y_pos ?? row.encounter_creatures.y_pos),
        z_pos: value(row.position?.z_pos ?? row.encounter_creatures.z_pos),
        movement_pct: value(row.encounter_creatures.movement_pct), aps: value(row.encounter_creatures.aps),
        rev: command.expected_rev + 1,
      };
    } catch (error) {
      // The optimistic apply threw before ownership was established: the entry must never be
      // transported (Codex review 2026-09-06). The caller undoes any partial mutation.
      const index = this.entries.indexOf(entry);
      if (index >= 0) this.entries.splice(index, 1);
      throw error;
    }
    // pump reports network errors as delivery state and holds the failed head.
    void started.commit;
    return completion;
  }

  /** The sender can attach the result to U0 even when the original caller ignored enqueue's return value. */
  appliedResult(operationId: string): ActorApplyResult | undefined {
    return this.entries.find(entry => entry.command.operation_id === operationId)?.completion;
  }

  /** The caller has already installed and painted its decision. Never run its rules again,
   * including on retry. These entries do not invent a movement revision. */
  enqueueSave(command: { schema: string; operation_id: string }, save: AppliedTruthSave): ActorApplyResult {
    const fingerprint = JSON.stringify(command);
    const existing = this.entries.find(entry => entry.command.operation_id === command.operation_id);
    if (existing) {
      if (existing.fingerprint !== fingerprint) throw new Error('Movement operation id reused with different data');
      return existing.completion;
    }
    let resolve!: (result: unknown) => void;
    const completion: ActorApplyResult = { commit: new Promise(done => { resolve = done; }), computed: {} };
    const envelope = { ...command, encounter_id: this.options.encounterId, actor_id: this.options.actorId,
      expected_rev: Number(this.options.row().encounter_creatures.movement_rev ?? 0), before: {} } as C;
    this.entries.push({ command: envelope, fingerprint, save, resolve, completion,
      delivery: { operationId: command.operation_id, commit: 'queued', errors: {} } });
    this.report(this.entries[this.entries.length - 1]!);
    void afterActionPaint(() => this.pump());
    return completion;
  }

  /** Entries still owed a server outcome (queued, sending, or network-failed).
   * Busy gates exclude saves whose decisions the caller has already installed. */
  pending({ includeAppliedSaves = true }: { includeAppliedSaves?: boolean } = {}): number {
    return this.entries.filter(entry => (includeAppliedSaves || !entry.save)
      && entry.delivery.commit !== 'delivered' && !this.isTerminal(entry)).length;
  }

  /** Snapshot the installed saves owed now, without replaying them or awaiting later saves.
   * No promise on the ordinary path; refusal or a parked queue cancels the wait, not the save. */
  settleAppliedSaves(): Promise<boolean> | null {
    const saves = this.entries.filter(entry => entry.save && entry.delivery.commit !== 'delivered' && !this.isTerminal(entry));
    if (!saves.length) return null;
    if (this.blocked()) return Promise.resolve(false);
    return new Promise(resolve => {
      const finish = (ok: boolean): void => { this.appliedSaveWaiters.delete(parked); resolve(ok); };
      const parked = (): void => {
        if (this.blocked() && saves.some(entry => entry.delivery.commit !== 'delivered' && !this.isTerminal(entry))) finish(false);
      };
      this.appliedSaveWaiters.add(parked);
      void Promise.all(saves.map(entry => entry.completion.commit)).then(results => finish(results.every(result => asRow(result)?.ok === true)));
    });
  }
  /** A terminal refusal/stale answer wrote nothing. An owed consequence may replan the same identity after reconciliation. */
  discardTerminal(operationId: string): boolean {
    const index = this.entries.findIndex(entry => entry.command.operation_id === operationId && this.isTerminal(entry));
    if (index < 0) return false;
    this.entries.splice(index, 1);
    return true;
  }

  /** The head owed an outcome is a network-failed delivery waiting for an explicit retry: nothing behind it moves
   * until the user retries, so a caller waiting for this queue to drain must stop waiting. */
  blocked(): boolean {
    const head = this.entries.find(entry => entry.delivery.commit !== 'delivered' && !this.isTerminal(entry));
    return head?.delivery.commit === 'failed';
  }

  retry(transport: MovementTransport): void {
    if (transport !== 'commit') return;
    const head = this.entries.find(entry => entry.delivery.commit !== 'delivered' && !this.isTerminal(entry));
    if (!head || head.delivery.commit !== 'failed') return;
    head.delivery.commit = 'queued';
    delete head.delivery.errors.commit;
    void this.pump();
  }

  private isTerminal(entry: Entry<C>): boolean {
    const error = entry.delivery.errors.commit as Partial<MovementTruthRejectedResult & MovementTruthTerminalInfo> | undefined;
    return entry.delivery.commit === 'failed' && (error?.error === 'stale' || error?.terminal === true);
  }

  private report(entry: Entry<C>): void {
    for (const parked of this.appliedSaveWaiters) parked();
    if (this.blocked()) for (const waiting of this.entries) {
      if (waiting.save && waiting.delivery.commit !== 'delivered' && !this.isTerminal(waiting)) {
        try { waiting.save.parked?.(); } catch { /* waiter only; retain retry delivery */ }
      }
    }
    // UI reporting must not turn a successful commit into a transport failure.
    try { this.options.changed({ ...entry.delivery, errors: { ...entry.delivery.errors } }); } catch { /* observer only */ }
  }

  private prune(): void {
    // Retain at most 256 completed/reconciled operation ids for duplicate reuse.
    // A pending or retryable network failure is never evicted.
    while (this.entries.length > 256 && this.entries[0]
      && (this.entries[0].delivery.commit === 'delivered' || this.isTerminal(this.entries[0]))) this.entries.shift();
  }

  /** Follow actual revision dependencies, rather than queue position alone. Failed entries
   * contribute their unwritten optimistic revision to the chain. EVERY invalidated entry
   * gets its schema's `failed` cleanup — a stale head too (Codex M1c review A2, finding 7): a
   * Dodge queued behind a refused walk must drop its status, not just its delivery. The adapters
   * restore shared scalars from the confirmed snapshot, so the order of these cleanups is immaterial. */
  private invalidateChain(head: Entry<C>, outcome: Record<string, unknown>, restedRev?: number): void {
    const from = this.entries.indexOf(head) + 1;
    const unwritten = new Set<number>();
    if (restedRev !== head.command.expected_rev + 1) unwritten.add(head.command.expected_rev + 1);
    const retained: Entry<C>[] = [];
    let invalidated = false;
    for (const entry of this.entries.slice(from)) {
      if (entry.delivery.commit === 'delivered' || this.isTerminal(entry)) continue;
      if (entry.command.actor_id !== head.command.actor_id || entry.command.encounter_id !== head.command.encounter_id) continue;
      if (entry.save || head.save) {
        const chained = { ...outcome, terminal: true, chained: true } as unknown as MovementTruthTerminalInfo;
        entry.delivery.commit = 'failed'; entry.delivery.errors.commit = chained;
        if (entry.save) void entry.save.failed(chained);
        else { try { this.options.failed?.(entry.command, chained); } catch { /* observer only */ } }
        entry.resolve(chained); this.report(entry);
        continue;
      }
      // A walk planned before a rest that DID advance the saved revision is genuinely stale.
      // Never rebase its resolved AP/position silently; it must be planned again from saved truth.
      const beforeRest = restedRev !== undefined && entry.command.expected_rev <= head.command.expected_rev
        && entry.command.expected_rev !== restedRev;
      if (!unwritten.has(entry.command.expected_rev) && !beforeRest) {
        // A retained branch will write this revision itself; its tail depends on that
        // command, rather than the background rest's abandoned optimistic revision.
        unwritten.delete(entry.command.expected_rev + 1);
        retained.push(entry);
        continue;
      }
      unwritten.add(entry.command.expected_rev + 1);
      invalidated = true;
      const chained = { ...outcome, chained: true };
      entry.delivery.commit = 'failed'; entry.delivery.errors.commit = chained;
      observeLocalTruthCommit('actor', entry.command, 'refused', asRow(outcome.body)?.reason ?? outcome.error);
      if (this.options.failed) {
        try { this.options.failed(entry.command, chained as unknown as MovementTruthTerminalInfo); } catch { /* observer only */ }
      }
      entry.resolve(chained); this.report(entry);
    }
    // Failure cleanup restores the shared confirmed row. The last retained entry's
    // projection includes its whole branch (walks AND subsequent resources/actions).
    // Reinstall only after cleanup, without re-running gameplay or schema side effects.
    const projection = retained[retained.length - 1]?.projection;
    if (invalidated && projection) applyMovementTruth(this.options.row(), projection);
  }

  private async pump(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      for (;;) {
        const entry = this.entries.find(item => item.delivery.commit !== 'delivered' && !this.isTerminal(item));
        if (!entry || entry.delivery.commit === 'failed') return;
        entry.delivery.commit = 'sending';
        observeLocalTruthCommit('actor', entry.command, 'sending');
        this.report(entry);
        try {
          const result = await (entry.save ? entry.save.commit() : this.options.commit(entry.command));
          if (entry.save) {
            const answer = asRow(result);
            if (answer?.ok !== true) throw new MovementTruthTerminal(Number(answer?.status ?? 409), String(answer?.error ?? 'save refused'), answer);
            observeLocalTruthCommit('actor', entry.command, 'accepted');
            entry.delivery.commit = 'delivered'; entry.resolve(result); this.report(entry); this.prune(); this.settle();
            continue;
          }
          const accepted = isMovementTruthAcceptedResult(result), response = asRow(result);
          observeLocalTruthCommit('actor', entry.command, accepted ? 'accepted' : 'refused', accepted
            ? ((result as Partial<AlreadyRestedResult>).alreadyRested === true ? 'already-rested' : undefined)
            : response?.reason ?? response?.error ?? 'result-not-accepted');
          if (accepted) {
            if ((result as Partial<AlreadyRestedResult>).alreadyRested === true) {
              this.invalidateChain(entry, { ok: false, error: 'stale', rev: result.rev, terminal: true }, result.rev);
            }
            try { this.options.accepted(entry.command, result); } catch { /* reconciliation observer */ }
          }
          entry.delivery.commit = 'delivered';
          entry.resolve(result); this.report(entry); this.prune(); this.settle();
        } catch (error) {
          if (error instanceof MovementTruthRejected) {
            observeLocalTruthCommit('actor', entry.command, 'stale', error.result.error);
            entry.delivery.commit = 'failed'; entry.delivery.errors.commit = error.result;
            this.invalidateChain(entry, error.result as unknown as Record<string, unknown>);
            try { this.options.rejected(entry.command, error.result); } catch { /* reconciliation observer */ }
            entry.resolve(error.result); this.report(entry); this.prune(); this.settle();
            continue;
          }
          if (error instanceof MovementTruthTerminal) {
            if (!entry.save && error.status === 409 && (error.body?.already_done === true || error.message === 'operation id reused')) {
              const result = { ok: true, already_done: true, replay: true };
              observeLocalTruthCommit('actor', entry.command, 'accepted', 'already-done');
              entry.delivery.commit = 'delivered'; entry.resolve(result); this.report(entry); this.prune(); this.settle();
              continue;
            }
            observeLocalTruthCommit('actor', entry.command, 'refused', error.body?.reason ?? error.message);
            const info: MovementTruthTerminalInfo = { error: error.message, status: error.status, terminal: true, ...(error.body ? { body: error.body } : {}) };
            entry.delivery.commit = 'failed'; entry.delivery.errors.commit = info;
            if (entry.save) {
              this.invalidateChain(entry, info as unknown as Record<string, unknown>);
              try { await entry.save.failed(info); } catch { /* reconciliation observer */ }
            } else {
              if (this.options.failed) { try { this.options.failed(entry.command, info); } catch { /* observer only */ } }
              this.invalidateChain(entry, info as unknown as Record<string, unknown>);
            }
            entry.resolve(info); this.report(entry); this.prune(); this.settle();
            continue;
          }
          entry.delivery.commit = 'failed'; entry.delivery.errors.commit = error;
          observeLocalTruthCommit('actor', entry.command, 'failed');
          this.report(entry); this.scheduleAutoRetry(entry); return;
        }
      }
    } finally { this.running = false; }
  }
}

declare global { var ftActionComputed: { operationId: string; delta: ActionComputedDelta } | undefined; }
